import React, { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from '@apollo/client'
import Button from '../../../components/Button/Button'
import Card from '../../../components/Card/Card'
import ComboInput from '../../../components/ComboInput'
import Modal from '../../../components/Modal/Modal'
import Notification from '../../../components/Notification/Notification'
import { WORLD_CUP_STAGES, WORLD_CUP_TEAMS } from '../../../graphql/queries'
import { UPDATE_WORLD_CUP_TEAMS } from '../../../graphql/mutations'
import {
  getTeamFlagUrl,
  groupStageLabel,
  suggestFlagCode,
  WORLD_CUP_TEAM_SUGGESTIONS,
  GROUP_STAGE_NAMES,
} from '../../WorldCup2026/worldCupTeamFlags'
import { emptyTeam } from './worldCupAdmin.utils'
import useNotification from '../../../hooks/useNotification'

export default function WorldCupTeamsAdmin() {
  const { showNotification, notificationProps } = useNotification()
  const [teamInputs, setTeamInputs] = useState([])
  const [teamModal, setTeamModal] = useState({ open: false, editIndex: null })
  const [teamDraft, setTeamDraft] = useState(emptyTeam())

  const { data: teamsData, refetch: refetchTeams } = useQuery(WORLD_CUP_TEAMS, {
    fetchPolicy: 'cache-and-network',
  })
  const { data: stagesData } = useQuery(WORLD_CUP_STAGES, {
    fetchPolicy: 'cache-and-network',
  })

  const groupOptions = useMemo(() => {
    const stages = stagesData?.worldCupStages
    if (!Array.isArray(stages)) return []
    return stages.filter((s) => s.name.startsWith('Group ')).map((s) => s.name)
  }, [stagesData])

  const savedTeams = useMemo(() => {
    const fromDb = teamsData?.worldCupTeams?.teams
    return Array.isArray(fromDb) ? fromDb : []
  }, [teamsData])

  const teamNameSuggestions = useMemo(() => {
    const names = new Set(WORLD_CUP_TEAM_SUGGESTIONS)
    savedTeams.forEach((t) => { if (t.name) names.add(t.name) })
    teamInputs.forEach((t) => { if (t.name?.trim()) names.add(t.name.trim()) })
    return [...names].sort((a, b) => a.localeCompare(b, 'da-DK'))
  }, [savedTeams, teamInputs])

  useEffect(() => {
    const saved = teamsData?.worldCupTeams?.teams
    if (!Array.isArray(saved)) return
    if (saved.length > 0) {
      setTeamInputs(saved.map((t) => ({
        name: t.name || '',
        group: t.group || '',
        flagCode: t.flagCode || suggestFlagCode(t.name) || '',
      })))
    } else {
      setTeamInputs([])
    }
  }, [teamsData])

  const [saveTeams, { loading: savingTeams }] = useMutation(UPDATE_WORLD_CUP_TEAMS, {
    onCompleted: async () => {
      showNotification('VM-hold gemt', 'success')
      await refetchTeams()
    },
    onError: (e) => showNotification(e.message || 'Kunne ikke gemme hold', 'error'),
  })

  const openAddTeamModal = () => {
    setTeamDraft(emptyTeam())
    setTeamModal({ open: true, editIndex: null })
  }

  const openEditTeamModal = (index) => {
    const team = teamInputs[index]
    if (!team) return
    setTeamDraft({
      name: team.name || '',
      group: team.group || '',
      flagCode: team.flagCode || suggestFlagCode(team.name) || '',
    })
    setTeamModal({ open: true, editIndex: index })
  }

  const closeTeamModal = () => {
    setTeamModal({ open: false, editIndex: null })
    setTeamDraft(emptyTeam())
  }

  const handleTeamDraftChange = (field, value) => {
    setTeamDraft((prev) => {
      const next = { ...prev, [field]: value }
      if (field === 'name') {
        next.flagCode = suggestFlagCode(value) || next.flagCode || ''
      }
      return next
    })
  }

  const submitTeamModal = () => {
    const name = teamDraft.name.trim()
    if (!name) {
      showNotification('Holdnavn er påkrævet', 'error')
      return
    }
    const duplicate = teamInputs.some(
      (t, i) => i !== teamModal.editIndex && t.name.trim().toLowerCase() === name.toLowerCase()
    )
    if (duplicate) {
      showNotification('Et hold med det navn findes allerede', 'error')
      return
    }
    const entry = {
      name,
      group: teamDraft.group?.trim() || '',
      flagCode: teamDraft.flagCode?.trim() || suggestFlagCode(name) || '',
    }
    if (teamModal.editIndex === null) {
      setTeamInputs((prev) => [...prev, entry])
    } else {
      setTeamInputs((prev) => prev.map((t, i) => (i === teamModal.editIndex ? entry : t)))
    }
    closeTeamModal()
  }

  const handleRemoveTeam = (index) => {
    setTeamInputs((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSaveTeams = async () => {
    const validTeams = teamInputs
      .map((t) => ({
        name: t.name.trim(),
        group: t.group?.trim() || null,
        flagCode: t.flagCode?.trim() || suggestFlagCode(t.name.trim()) || null,
      }))
      .filter((t) => t.name)

    if (validTeams.length === 0) {
      showNotification('Tilføj mindst ét hold', 'error')
      return
    }
    const unique = new Set(validTeams.map((t) => t.name.toLowerCase()))
    if (unique.size !== validTeams.length) {
      showNotification('Holdnavne skal være unikke', 'error')
      return
    }
    await saveTeams({ variables: { teams: validTeams } })
  }

  const validTeamCount = teamInputs.filter((t) => t.name.trim()).length
  const teamDraftFlagUrl = getTeamFlagUrl(teamDraft.name, teamDraft.flagCode)
  const isEditingTeam = teamModal.editIndex !== null

  const teamsByGroup = useMemo(() => {
    const sections = GROUP_STAGE_NAMES.map((groupName) => ({
      key: groupName,
      label: groupStageLabel(groupName),
      teams: teamInputs
        .map((team, index) => ({ team, index }))
        .filter(({ team }) => team.group === groupName),
    })).filter((section) => section.teams.length > 0)

    const ungrouped = teamInputs
      .map((team, index) => ({ team, index }))
      .filter(({ team }) => !team.group || !GROUP_STAGE_NAMES.includes(team.group))

    if (ungrouped.length > 0) {
      sections.push({ key: 'ungrouped', label: 'Uden gruppe', teams: ungrouped })
    }

    return sections
  }, [teamInputs])

  const renderTeamItem = ({ team, index }) => {
    const flagUrl = getTeamFlagUrl(team.name, team.flagCode)
    return (
      <div key={`${team.name}-${index}`} className="worldcup-admin-team-item">
        <span className="worldcup-admin-team-flag">
          {flagUrl ? (
            <img src={flagUrl} alt="" className="worldcup-admin-team-flag-img" />
          ) : (
            <span className="worldcup-admin-team-flag-missing" title="Intet flag fundet">?</span>
          )}
        </span>
        <div className="worldcup-admin-team-info">
          <strong>{team.name}</strong>
        </div>
        <div className="worldcup-admin-team-item-actions">
          <Button title="Rediger" variant="outlined" onClick={() => openEditTeamModal(index)} />
          <button
            type="button"
            className="worldcup-admin-remove-team"
            onClick={() => handleRemoveTeam(index)}
            aria-label={`Fjern ${team.name}`}
          >
            ×
          </button>
        </div>
      </div>
    )
  }

  return (
    <>
      <Notification {...notificationProps} />
      <Card className="worldcup-admin-card" hover={false}>
        <h3>VM-hold</h3>
        <p className="worldcup-admin-help">
          Tilføj hold via modalen — du slipper for at scrolle i en lang liste.
          Begynd at skrive holdnavn for at se forslag (officielle VM-navne og play-off-pladser).
          Tildel valgfrit en gruppe (Group A–L). Flag hentes automatisk; ret ISO-koden manuelt hvis nødvendigt.
        </p>
        <div className="worldcup-admin-teams-header">
          <span className="worldcup-admin-teams-count">{validTeamCount} hold</span>
          <Button title="+ Tilføj hold" onClick={openAddTeamModal} variant="outlined" />
        </div>
        <div className="worldcup-admin-teams-list">
          {teamInputs.length === 0 ? (
            <div className="worldcup-admin-teams-empty">
              Ingen hold endnu. Klik «Tilføj hold» for at komme i gang.
            </div>
          ) : (
            teamsByGroup.map((groupSection) => (
              <section key={groupSection.key} className="worldcup-admin-team-group">
                <h4 className="worldcup-admin-team-group-title">{groupSection.label}</h4>
                <div className="worldcup-admin-team-group-list">
                  {groupSection.teams.map(renderTeamItem)}
                </div>
              </section>
            ))
          )}
        </div>
        <div className="worldcup-admin-form-actions">
          <Button title="Gem VM-hold" onClick={handleSaveTeams} color="confirmation" loading={savingTeams} loadingText="Gemmer..." />
        </div>
      </Card>

      <Modal
        isOpen={teamModal.open}
        onClose={closeTeamModal}
        title={isEditingTeam ? 'Rediger hold' : 'Tilføj hold'}
        size="small"
        className="worldcup-admin-modal"
      >
        <div className="worldcup-admin-team-modal">
          <div className="worldcup-admin-team-modal-flag">
            {teamDraftFlagUrl ? (
              <img src={teamDraftFlagUrl} alt="" className="worldcup-admin-team-flag-img worldcup-admin-team-flag-img--large" />
            ) : (
              <span className="worldcup-admin-team-flag-missing worldcup-admin-team-flag-missing--large" title="Intet flag fundet">?</span>
            )}
          </div>
          <label className="worldcup-admin-field">
            <span className="worldcup-admin-label">Hold</span>
            <ComboInput
              id="wc-team-modal-name"
              value={teamDraft.name}
              options={teamNameSuggestions}
              placeholder="Søg eller indtast holdnavn"
              ariaLabel="Holdnavn"
              emptyMessage="Ingen matchende hold — tryk videre for at bruge eget navn"
              onChange={(v) => handleTeamDraftChange('name', v)}
              onInputChange={(v) => handleTeamDraftChange('name', v)}
            />
          </label>
          <label className="worldcup-admin-field">
            <span className="worldcup-admin-label">Flag (ISO-kode)</span>
            <input
              className="worldcup-admin-input worldcup-admin-input--flag"
              type="text"
              value={teamDraft.flagCode || ''}
              placeholder="fx mx, gb-eng"
              onChange={(e) => handleTeamDraftChange('flagCode', e.target.value)}
            />
          </label>
          <label className="worldcup-admin-field">
            <span className="worldcup-admin-label">Gruppe</span>
            <select
              className="worldcup-admin-input"
              value={teamDraft.group || ''}
              onChange={(e) => handleTeamDraftChange('group', e.target.value)}
            >
              <option value="">Ingen gruppe</option>
              {groupOptions.map((group) => (
                <option key={group} value={group}>{group}</option>
              ))}
            </select>
          </label>
          <div className="worldcup-admin-team-modal-actions">
            <Button title="Annuller" variant="outlined" className="worldcup-admin-modal-cancel" onClick={closeTeamModal} />
            <Button
              title={isEditingTeam ? 'Gem ændringer' : 'Tilføj hold'}
              color="confirmation"
              onClick={submitTeamModal}
            />
          </div>
        </div>
      </Modal>
    </>
  )
}
